import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import SearchBar from '../../../../common/formfields/SearchBar';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UnitList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const fetchUnits = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ResidentialUnitMaster/GetAllUnitMaster`
            });

            if (response.isSuccess || response.success || response.IsSuccess || response) {
                let apiData = response.data || response;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.ResidentialUnitData)) {
                    data = apiData.ResidentialUnitData;
                }

                setUnits(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching residential units:", error);
            toast.error("Failed to fetch units.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUnits();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const tableHeaders = [
        { label: "NO", key: "no" },
        { label: "SITE", key: "siteName" },
        { label: "WING", key: "wingName" },
        { label: "FLOOR", key: "floorName" },
        { label: "UNIT/FLAT", key: "unitName" },
        { label: "AREA", key: "sqft" },
        { label: "STATUS", key: "isActive" },
        { label: "ACTIONS", key: "actions" }
    ];

    const renderCell = (key, row, index) => {
        const val = (k) => row[k] || row[k.charAt(0).toUpperCase() + k.slice(1)] || "—";
        switch (key) {
            case "no":
                return <span className="text-xs font-semibold text-[#1E293B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "siteName":
            case "wingName":
            case "floorName":
            case "unitName":
            case "sqft":
                return <span className="text-xs font-semibold text-[#1E293B]">{val(key)}</span>;
            case "isActive":
                const status = row.isActive || row.IsActive || (row.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black shadow-sm uppercase ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return (
                    <div className="flex gap-2 justify-center">
                        <button
                            onClick={() => navigate(`/zentres/master-management/unit/form`, { state: { id: row.unitId || row.UnitId, item: row } })}
                            className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center transition-colors"
                            title="Edit Unit"
                        >
                            <FontAwesomeIcon icon={faPen} className="text-sm" />
                        </button>
                    </div>
                );
            default:
                return <span className="text-xs font-normal text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const filteredData = useMemo(() => {
        let data = units;
        if (statusFilter) {
            data = data.filter(item => {
                const s = item.isActive || item.IsActive || (item.isActiveId === 1 ? 'Active' : 'Inactive');
                return s === statusFilter;
            });
        }
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            data = data.filter(item =>
                (item.unitName || item.UnitName || "").toLowerCase().includes(query) ||
                (item.floorName || item.FloorName || item.floorNumber || item.FloorNumber || "").toString().toLowerCase().includes(query) ||
                (item.wingName || item.WingName || "").toLowerCase().includes(query) ||
                (item.siteName || item.SiteName || "").toLowerCase().includes(query)
            );
        }
        return data;
    }, [units, statusFilter, searchQuery]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <SearchBar
                placeholder="Search units..."
                value={searchQuery}
                onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-48"
            />
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 bg-white"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Residential Unit Management"
                subHeading="Manage all units/flats within your residential floors"
                primaryBtnText="Create Unit"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate('/zentres/master-management/unit/form')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Units List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData}
                    renderCell={(key, row) => renderCell(key, row, paginatedData.indexOf(row))}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    showActionCol={false}
                />
            </div>
        </div>
    );
};

export default UnitList;
